import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';

export const MODES = {
  FOCUS: 'focus',
  SHORT_BREAK: 'short_break',
  LONG_BREAK: 'long_break',
};

const getDurationSecs = (mode, settings) => {
  if (mode === MODES.FOCUS) return settings.focusDuration * 60;
  if (mode === MODES.SHORT_BREAK) return settings.shortBreakDuration * 60;
  return settings.longBreakDuration * 60;
};

/**
 * @param {{ settings: object, onComplete: Function, restored?: object | null, preferredMode?: string | null }} opts
 */
const useTimer = ({ settings, onComplete, restored = null, preferredMode = null }) => {
  const pref = preferredMode && Object.values(MODES).includes(preferredMode) ? preferredMode : null;
  const initialMode = restored?.mode ?? pref ?? MODES.FOCUS;
  const cap = getDurationSecs(initialMode, settings);
  const initialRemaining = restored?.remaining != null
    ? Math.min(restored.remaining, cap)
    : cap;
  const initialSession = restored?.sessionCount ?? 0;

  const [mode, setMode] = useState(initialMode);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionCount, setSessionCount] = useState(initialSession);
  const [remaining, setRemaining] = useState(() =>
    Math.min(initialRemaining, getDurationSecs(initialMode, settings)),
  );

  const isRunningRef = useRef(false);
  const startTimeRef = useRef(null);
  const startRemRef = useRef(Math.min(initialRemaining, getDurationSecs(initialMode, settings)));
  const modeRef = useRef(initialMode);
  const sessionCountRef = useRef(initialSession);
  const settingsRef = useRef(settings);
  const onCompleteRef = useRef(onComplete);
  const rafRef = useRef(null);
  const restoredRunRef = useRef(!!restored?.autoRun);

  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const stopRaf = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  const tick = useCallback(() => {
    if (!isRunningRef.current || startTimeRef.current === null) return;
    const elapsed = (Date.now() - startTimeRef.current) / 1000;
    const rem = Math.max(0, startRemRef.current - elapsed);
    setRemaining(rem);
    if (rem <= 0) {
      isRunningRef.current = false;
      setIsRunning(false);
      startTimeRef.current = null;
      onCompleteRef.current(modeRef.current, sessionCountRef.current, false);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useLayoutEffect(() => {
    if (!restoredRunRef.current) return;
    restoredRunRef.current = false;
    const rem = Math.max(0, startRemRef.current);
    if (rem <= 0) return;
    isRunningRef.current = true;
    startTimeRef.current = Date.now();
    setIsRunning(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const start = useCallback(() => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    startTimeRef.current = Date.now();
    setIsRunning(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const pause = useCallback(() => {
    if (!isRunningRef.current) return;
    const elapsed = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0;
    const rem = Math.max(0, startRemRef.current - elapsed);
    startRemRef.current = rem;
    startTimeRef.current = null;
    isRunningRef.current = false;
    setIsRunning(false);
    setRemaining(rem);
    stopRaf();
  }, []);

  const reset = useCallback(() => {
    stopRaf();
    isRunningRef.current = false;
    startTimeRef.current = null;
    setIsRunning(false);
    const dur = getDurationSecs(modeRef.current, settingsRef.current);
    startRemRef.current = dur;
    setRemaining(dur);
  }, []);

  const skip = useCallback(() => {
    stopRaf();
    isRunningRef.current = false;
    startTimeRef.current = null;
    setIsRunning(false);
    onCompleteRef.current(modeRef.current, sessionCountRef.current, true);
  }, []);

  const changeMode = useCallback((newMode) => {
    stopRaf();
    isRunningRef.current = false;
    startTimeRef.current = null;
    setIsRunning(false);
    setMode(newMode);
    modeRef.current = newMode;
    const dur = getDurationSecs(newMode, settingsRef.current);
    startRemRef.current = dur;
    setRemaining(dur);
  }, []);

  const updateRemaining = useCallback((newSettings) => {
    if (!isRunningRef.current) {
      const dur = getDurationSecs(modeRef.current, newSettings);
      startRemRef.current = dur;
      setRemaining(dur);
    }
  }, []);

  const updateSessionCount = useCallback((count) => {
    setSessionCount(count);
    sessionCountRef.current = count;
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && isRunningRef.current && startTimeRef.current) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const rem = Math.max(0, startRemRef.current - elapsed);
        setRemaining(rem);
        if (rem <= 0) {
          isRunningRef.current = false;
          setIsRunning(false);
          onCompleteRef.current(modeRef.current, sessionCountRef.current, false);
        } else {
          stopRaf();
          rafRef.current = requestAnimationFrame(tick);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [tick]);

  useEffect(() => () => stopRaf(), []);

  const totalDuration = getDurationSecs(mode, settings);
  const progress = totalDuration > 0 ? 1 - remaining / totalDuration : 0;

  return {
    mode, changeMode,
    isRunning, remaining, progress,
    sessionCount, setSessionCount: updateSessionCount,
    start, pause, reset, skip,
    updateRemaining,
    totalDuration,
  };
};

export default useTimer;
